import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal, computed } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { environment } from '@environments/environment';

import { ProbateService } from '@app/probate/shared/services/probate.service';
import { ProbateSections } from '@app/core/probate/models/probate-sections.models';
import { CourtService } from '@shared/services/court.service';
import { StorageService } from '@shared/services/storage.service';
import { StorageKey } from '@shared/enums/storage-keys.enum';
import { EventManager } from '@app/core/observer';
import { EventListener } from '@app/core/observer/event.listener';
import { ObserverEvents } from '@app/core/observer/events';
import { GenericApplicationSections } from '@shared/interfaces/agnostic-sections.type';
import { TenantFacadeService } from '@shared/features/tenant/shared/services/tenant-facade.service';

@Injectable({
  providedIn: 'root',
})
export class SectionsService implements EventListener {
  private _http = inject(HttpClient);
  private _courtService = inject(CourtService);
  private _probateService = inject(ProbateService);
  private _tenantService = inject(TenantFacadeService);
  private _storage = inject(StorageService);
  private _eventManager = inject(EventManager);

  constructor() {
    this._eventManager.subscribe(ObserverEvents.LOGOUT, this);
  }

  availableSections = signal<GenericApplicationSections[]>(
    this._storage.read(StorageKey.availableSections) || [],
  );
  lockedSections = signal<GenericApplicationSections[]>([]);
  private _lockedLoaded = false;

  readonly _courtId = computed(() => this._courtService.courtId());
  readonly _probateId = computed(() => this._probateService.probate()?.id);

  isSectionLocked(section: GenericApplicationSections) {
    return this.lockedSections().includes(section);
  }

  update(eventType: ObserverEvents) {
    if (eventType === ObserverEvents.LOGOUT) {
      this.availableSections.set([]);
      this._storage.remove(StorageKey.availableSections);
    }
  }

  getAvailableSubscriberSections(): Observable<GenericApplicationSections[]> {
    return this._http
      .get<
        GenericApplicationSections[]
      >(`${environment.api}/courts/${this._courtId()}/${this._tenantService.tenantApiPrefix()}/sections/`)
      .pipe(
        tap((sections) => {
          this.availableSections.set(sections);
          this._storage.save(StorageKey.availableSections, sections);
          return this.availableSections();
        }),
      );
  }

  getLockedSections(): Observable<ProbateSections[]> {
    return this._http
      .get<
        ProbateSections[]
      >(`${environment.api}/probates/${this._probateId()}/sections-locked`)
      .pipe(
        tap((sections) => {
          this.lockedSections.set(sections);
          this._lockedLoaded = true;
          return this.lockedSections();
        }),
      );
  }

  ensureLockedSectionsLoaded(): void {
    if (!this._lockedLoaded) {
      this.getLockedSections().subscribe();
    }
  }

  lockSection(section: GenericApplicationSections) {
    return this._http
      .post<GenericApplicationSections>(
        `${environment.api}/probates/${this._probateId()}/sections/${section}/lock/`,
        {},
      )
      .pipe(
        tap(() => {
          this.getLockedSections();
        }),
      );
  }

  unlockSection(section: GenericApplicationSections) {
    return this._http
      .post<GenericApplicationSections>(
        `${environment.api}/probates/${this._probateId()}/sections/${section}/unlock/`,
        {},
      )
      .pipe(
        tap(() => {
          this.getLockedSections();
        }),
      );
  }
}
